import { useEffect, useId, useRef, type FocusEvent } from "react";
import { ClipboardPaste } from "lucide-react";

import { Button } from "@/components/ui/button";

interface PasteDetectedModalProps {
  /** Called on every dismissal path — click anywhere, Enter/Space on the
   *  button, Escape, focus leaving the dialog. Idempotent by contract: the
   *  owner just sets its `open` flag to false. */
  onDismiss: () => void;
}

/**
 * "Copy & pasting detected" — the candidate-facing deterrent shown after a
 * paste (clipboard or drag-and-drop) lands in the code editor. Adapted from
 * Coderbyte's cheat-proof notice.
 *
 * A NOTICE, NOT A BLOCK. In-editor paste is deliberately allowed so candidates
 * can move their own code around (docs/features/anticheat.md, Part 1). The
 * anti-cheat controller records the `paste` event exactly as before and only
 * then — after the native dispatch has fully completed, so the text is already
 * in the editor — asks Question.tsx to mount this. Nothing here emits, blocks
 * or gates; anti-cheat is never plan-gated, so there is no setting either.
 *
 * Dismissal: click ANYWHERE (backdrop or card — the card does not stop
 * propagation), Enter/Space on the focused "Continue" button, Escape, or focus
 * leaving the dialog. Escape works but is NOT advertised in the copy: in a
 * real test the candidate is in fullscreen, where Esc also exits fullscreen,
 * and a single `fullscreen_exit` flags the attempt (backend/risk.py) — the
 * hint must not steer them into that. The Escape listener is bubble-phase on
 * `window` and never preventDefault/stopPropagation, so useFullscreen's
 * capture-phase exit attribution keeps seeing the key.
 *
 * Focus is MOVED, never TRAPPED — the opposite of FullscreenBlockingOverlay,
 * which traps on purpose because it is an inescapable gate. Focus lands on the
 * button on open (Enter dismisses; screen readers land on the alertdialog) and
 * returns to whatever had it on close — restored from the saved
 * document.activeElement, never a selector: on Chromium Monaco focuses an
 * EditContext <div>, not `textarea.inputarea`. Tab is not contained; instead,
 * focus moving to any element OUTSIDE the dialog dismisses it (without the
 * restore — focus has a new, deliberate home), so the notice can never sit
 * over a control that holds keyboard focus behind the blurred backdrop.
 * `relatedTarget === null` (mousedown on the backdrop, Alt-Tab) is ignored.
 *
 * No portal: this must render inside CandidateRouteLayout's `translate="no"`
 * subtree (App.tsx) — that block is load-bearing (FRONTEND-REACT-8) and a
 * portal to <body> would escape it. Rendered BEFORE FullscreenBlockingOverlay
 * in Question.tsx, and hidden while that overlay is up, so the gate always
 * wins the stack.
 */
export function PasteDetectedModal({ onDismiss }: PasteDetectedModalProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  // Latest dismiss callback without re-subscribing the Escape listener on
  // every parent render (the owner passes an inline arrow).
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  // Cleared by the focus-out path: focus already moved somewhere deliberate,
  // so the unmount must not yank it back into the editor.
  const restoreFocusRef = useRef(true);
  // Set while THIS component moves focus (the restore in the cleanup). Focus
  // leaving the button then is our own doing, not the candidate tabbing out,
  // and must not read as a focus-out dismissal. React StrictMode's dev-only
  // mount → cleanup → mount double-invoke runs that restore mid-mount, and
  // without this guard the notice dismissed itself before it was ever painted
  // (caught in the 2026-09-06 browser run).
  const movingFocusRef = useRef(false);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const previous = document.activeElement;
    buttonRef.current?.focus();
    return () => {
      if (!restoreFocusRef.current) return;
      if (previous instanceof HTMLElement && previous.isConnected) {
        movingFocusRef.current = true;
        try {
          previous.focus();
        } finally {
          movingFocusRef.current = false;
        }
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onDismissRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const onBlur = (e: FocusEvent<HTMLDivElement>): void => {
    if (movingFocusRef.current) return;
    const next = e.relatedTarget;
    if (!(next instanceof Node)) return;
    if (rootRef.current?.contains(next)) return;
    restoreFocusRef.current = false;
    onDismissRef.current();
  };

  return (
    // Full-viewport backdrop. The WHOLE surface is the dismiss target, so the
    // pointer cursor is honest everywhere on it.
    <div
      ref={rootRef}
      className="fixed inset-0 z-50 flex cursor-pointer items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      onClick={onDismiss}
      onBlur={onBlur}
    >
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-lg border border-border border-l-4 border-l-destructive bg-card p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <ClipboardPaste
            className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1 space-y-4">
            <div className="space-y-1">
              <p
                id={titleId}
                className="text-base font-semibold text-foreground"
              >
                Copy &amp; pasting detected
              </p>
              <p id={descId} className="text-sm text-muted-foreground">
                To keep this assessment fair, copying and pasting is monitored.
                The paste you just made has been recorded and will be visible
                to the reviewer. Moving your own code around inside the editor
                is fine; pasting a solution from elsewhere is not.
              </p>
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Click anywhere or press Enter to continue.
              </p>
              {/* No onClick of its own — the click bubbles to the root's
                  onDismiss, so a button press never dismisses twice. */}
              <Button
                ref={buttonRef}
                type="button"
                className="shrink-0 cursor-pointer"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
